import { TextField } from '@/components/TextField';
import useDebounce from '@/hooks/useDebounce';
import { useSearchCourses } from '@/hooks/useSearchCourses';
import { useLocale } from '@/providers/LocaleProvider';
import AiSparkleIcon from '@/resources/images/icons-ai-sparkle.svg';
import CloseIcon from '@/resources/images/icons-close.svg';
import SearchIcon from '@/resources/images/icons-search.svg';
import { KLAYO_COLORS } from '@/themes';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AISparkleLoader } from '../AISparkleLoader';
import { Dialog } from './Dialog';

interface CourseFinderDialogProps {
  open: boolean;
  onClose: () => void;
  theme?: any;
  isTrainingManager?: boolean;
  /** Fired (debounced) when the learner searches — used for catalogue analytics. */
  onSearch?: (text: string) => void;
  /** Fired when a result is selected — used for catalogue analytics. `text` is the search query. */
  onResultSelected?: (courseName: string, text: string) => void;
}

export const CourseFinderDialog = ({
  open,
  onClose,
  theme,
  isTrainingManager,
  onSearch,
  onResultSelected,
}: CourseFinderDialogProps) => {
  const { translate } = useLocale();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 500);

  const { data, isLoading } = useSearchCourses(debouncedQuery);

  useEffect(() => {
    if (debouncedQuery.trim()) {
      onSearch?.(debouncedQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const results = data?.results ?? [];
  const hasQuery = !!debouncedQuery.trim();

  const handleCourseClick = (courseId: string) => {
    if (isTrainingManager) {
      navigate(`/settings/learning/${courseId}`);
    } else {
      navigate(`/my-klayo/courses/${courseId}`);
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      theme={theme}
      fullWidth
      title={translate('coursesPage.courseFinder')}
      maxWidth='md'
      closeButtonSx={{
        display: 'block',
      }}
      contentSx={{
        paddingBottom: { xs: '20px !important', md: '40px !important' },
      }}
    >
      <Stack spacing={2}>
        {/* Search input */}
        <Stack gap={1.5}>
          <TextField
            value={query}
            dense={true}
            placeholder={translate('coursesPage.courseFinderPlaceholder')}
            fullWidth={true}
            autoComplete={false}
            leadingIcon={<AiSparkleIcon color={KLAYO_COLORS.NeonBlueNB500} />}
            endAdornment={
              query ? (
                <IconButton size='small' onClick={() => setQuery('')} edge='end'>
                  <CloseIcon style={{ width: 16, height: 16 }} />
                </IconButton>
              ) : undefined
            }
            onChange={(e) => setQuery(e.target.value)}
          />
          <Stack direction='row' justifyContent='flex-end' alignItems='center' gap={0.5} mt={0.5}>
            <AiSparkleIcon
              color={KLAYO_COLORS.GreyGR400}
              style={{ width: 16, height: 16, flexShrink: 0 }}
            />
            <Typography variant='caption' sx={{ color: KLAYO_COLORS.GreyGR500 }}>
              {translate('coursesPage.courseFinderPoweredBy')}
            </Typography>
          </Stack>
        </Stack>

        {/* Results area */}
        <Box>
          {isLoading ? (
            <Box display='flex' justifyContent='center' py={8}>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1.5}
                justifyContent={'center'}
                p={4}
              >
                <AISparkleLoader />
                <Typography
                  variant='body1'
                  sx={{
                    color: 'var(--neon-blue-nb-300)',
                  }}
                >
                  {translate('onboarding.currentJob.performingMagic')}
                </Typography>
              </Stack>
            </Box>
          ) : !hasQuery ? (
            /* Empty state */
            <Stack alignItems='center' spacing={2} py={6} px={2}>
              <AiSparkleIcon style={{ width: 48, height: 48 }} color={KLAYO_COLORS.NeonBlueNB200} />
              <Stack alignItems='center' spacing={0.5}>
                <Typography variant='h6' fontSize={18} textAlign='center'>
                  {translate('coursesPage.courseFinderStartSearch')}
                </Typography>
                <Typography
                  variant='body2'
                  textAlign='center'
                  sx={{ color: KLAYO_COLORS.GreyGR500 }}
                >
                  {translate('coursesPage.courseFinderStartSearchDesc')}
                </Typography>
              </Stack>
            </Stack>
          ) : results.length === 0 ? (
            /* No results state */
            <Stack alignItems='center' spacing={1.5} py={6}>
              <SearchIcon style={{ width: 40, height: 40, color: KLAYO_COLORS.NeonBlueNB200 }} />
              <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR600 }}>
                {translate('coursesPage.courseFinderNoResults')}
              </Typography>
            </Stack>
          ) : (
            /* Results list */
            <Stack divider={<Divider sx={{ borderColor: KLAYO_COLORS.GreyGR200 }} />}>
              {results.map((course) => (
                <Stack
                  key={course.id}
                  gap={2}
                  p={2}
                  onClick={() => {
                    onResultSelected?.(course.title, debouncedQuery);
                    handleCourseClick(course.id);
                  }}
                  sx={{
                    cursor: 'pointer',
                  }}
                >
                  <Stack gap={0.5}>
                    <Typography variant='body1' fontWeight={500}>
                      {course.title}
                    </Typography>
                    {course.description && (
                      <Typography variant='body2' className='truncate'>
                        {course.description}
                      </Typography>
                    )}
                  </Stack>
                  {[
                    course.level,
                    course.courseType,
                    course.format,
                    course.averageRating != null
                      ? `\u2B50 ${course.averageRating.toFixed(1)}`
                      : null,
                  ].filter(Boolean).length ? (
                    <Stack direction='row' gap={0.5} flexWrap='wrap'>
                      {[
                        course.level,
                        course.courseType,
                        course.format,
                        course.averageRating != null
                          ? `\u2B50 ${course.averageRating.toFixed(1)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .map((meta, i, arr) => (
                          <Typography
                            key={i}
                            variant='caption'
                            sx={{ color: KLAYO_COLORS.GreyGR500 }}
                          >
                            {meta}
                            {i < arr.length - 1 ? '\u00A0\u2022' : ''}
                          </Typography>
                        ))}
                    </Stack>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Dialog>
  );
};
